/**
 * Which page a quote opens in.
 *
 * There is one builder now. Migration 0061 moved every v1 quote that could
 * move, which is every one nobody has approved; what is left is the approvals
 * themselves, kept in the shape the customer agreed to and shown read-only by
 * `QuoteArchive`. The quote's own `builder` column is still the switch,
 * because it is exactly the right condition — the new builder cannot draw an
 * option — but it now picks between a builder and an archive rather than
 * between two builders.
 *
 * When the last approved v1 quote is superseded, this file and the archive go
 * with it.
 */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import type { MeResponse } from '@rowted/shared'
import { request } from '../lib/api.js'
import { QuoteArchive } from './quote-archive.js'
import { QuoteBuilderV2 } from './quote-builder-v2.js'

export function QuoteRoute({ me }: { me: MeResponse }) {
  const { id = '' } = useParams()
  const [builder, setBuilder] = useState<'v1' | 'v2' | null>(null)

  useEffect(() => {
    let live = true
    void request<{ quote: { builder?: 'v1' | 'v2' } }>(`/quotes/${id}`)
      .then((payload) => { if (live) setBuilder(payload.quote?.builder ?? 'v2') })
      /*
       * A quote that will not load is the builder's problem to report, not
       * this file's — and v2 is the safe answer now, because everything
       * anybody can still work on is v2.
       */
      .catch(() => { if (live) setBuilder('v2') })
    return () => { live = false }
  }, [id])

  if (builder === null) return <p className="p-4 text-cell text-text-muted">Loading…</p>
  return builder === 'v2' ? <QuoteBuilderV2 me={me} /> : <QuoteArchive me={me} />
}
